import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { ArrowLeft, RefreshCw, TrendingDown, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Spinner } from '../ui/spinner';
import BrandSelect from '../ui/BrandSelect';
import QualityTierBadge from '../Quality/QualityTierBadge';
import { adminToolbarSelect } from '../utils/ReactSelectStyles';
import { Actions as ORG_ACTIONS } from '../../store/actions/organisationActions';
import { Actions as DASHBOARD_ACTIONS } from '../../store/actions/consultantDashboardActions';
import { toggleAlertMessage } from '../../store/action';
import { formatSnapshotTime } from './formatSnapshotTime';
import { formatLastActivity } from './formatLastActivity';
import { firmUserOwnerOptions } from './ownerDisplay';
import { formatCompletenessCell, resolveStatusBadge } from './operationalStatus';

const reportStatusLabel = {
  not_on_platform: 'Not on platform',
  not_started: 'Not started',
  in_progress: 'In progress',
  generated: 'Generated',
};

/**
 * Surface B — client health drill-down from portfolio snapshot API.
 * Account manager is set here (same path as Consultant Dashboard row click).
 */
const ClientHealthDashboard = ({
  health,
  loading = false,
  onBack,
  onRefresh,
  onOpenBilling,
  onOpenWorkspace,
  onOpenTask,
  organisationUsers,
  getOrganisationUsers,
  assignClientOwner,
  createWorkItem,
  setRespondingPartyAccess,
  signOffCompleteness,
  requestAssist,
  ownerLoading,
  showAlert,
}) => {
  const [ownerUserId, setOwnerUserId] = useState(null);
  const [ownerReady, setOwnerReady] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [accessBusy, setAccessBusy] = useState(false);

  useEffect(() => {
    getOrganisationUsers();
  }, [getOrganisationUsers]);

  useEffect(() => {
    if (!health) {
      setOwnerReady(false);
      return;
    }
    setOwnerUserId(health.ownerUserId || null);
    setOwnerReady(true);
  }, [health]);

  const ownerOptions = useMemo(() => firmUserOwnerOptions(organisationUsers), [organisationUsers]);
  const selectedOwner =
    ownerOptions.find((o) => o.value === (ownerUserId || '')) || ownerOptions[0];

  const handleOwnerChange = async (opt) => {
    const next = opt?.value || null;
    const clientOrgId = health?.clientOrgId;
    if (!clientOrgId) return;
    const result = await assignClientOwner(clientOrgId, next || null);
    if (result?.ok) {
      setOwnerUserId(next);
      showAlert(true, next ? 'Account manager updated.' : 'Account manager cleared.', 'success', 4000);
      onRefresh?.();
    } else {
      showAlert(true, result?.message || 'Could not update account manager.', 'error', 5000);
    }
  };

  if (loading && !health) {
    return (
      <div className='flex justify-center py-12'>
        <Spinner />
      </div>
    );
  }

  if (!health) {
    return (
      <div className='flex flex-col gap-3'>
        <Button variant='ghost' size='sm' onClick={onBack}>
          <ArrowLeft size={16} strokeWidth={1.5} />
          Back
        </Button>
        <p className='text-muted-foreground'>Client health not available.</p>
      </div>
    );
  }

  const yoy = health.yoy || {};
  const qualityScore = health.itemQuality?.score;
  const delta = yoy.deltaPct;
  const DeltaIcon = delta != null && delta < 0 ? TrendingDown : TrendingUp;
  const statusBadge = resolveStatusBadge(health);
  const lists = health.completenessLists || {};
  const completenessHeadline = formatCompletenessCell(health);

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex flex-wrap items-center gap-3'>
        <Button variant='ghost' size='sm' onClick={onBack}>
          <ArrowLeft size={16} strokeWidth={1.5} />
        </Button>
        <div className='flex-1 min-w-[200px]'>
          <h1 className='text-xl font-semibold text-foreground'>{health.name || 'Unnamed client'}</h1>
          <p className='text-sm text-muted-foreground'>
            Client health · snapshot {formatSnapshotTime(health.snapshotComputedAt)}
            {health.lastActiveAt
              ? ` · last activity ${formatLastActivity(health.lastActiveAt)}`
              : ' · last activity Never'}
            {health.reportingPeriodLabel || health.yoy?.reportingPeriodLabel
              ? ` · ${health.reportingPeriodLabel || health.yoy.reportingPeriodLabel}`
              : ''}
          </p>
          <div className='mt-1 flex flex-col gap-1 items-start'>
            <Badge className={statusBadge.className}>{statusBadge.label}</Badge>
            {statusBadge.lockReason && (
              <p className='text-xs text-muted-foreground'>{statusBadge.lockReason}</p>
            )}
          </div>
        </div>
        {onOpenWorkspace && (
          <Button size='sm' onClick={onOpenWorkspace}>
            Open client workspace
          </Button>
        )}
        <Button variant='outline' size='sm' onClick={onOpenBilling}>
          Billing detail
        </Button>
        <Button variant='outline' size='sm' onClick={onRefresh} disabled={loading}>
          <RefreshCw size={16} strokeWidth={1.5} />
          Refresh
        </Button>
      </div>

      <Card className='bg-card'>
        <CardContent className='p-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between'>
          <div>
            <p className='text-sm font-medium text-foreground'>Account manager</p>
            <p className='text-xs text-muted-foreground'>
              Your firm’s team member for this client (not the client org’s own admin).
            </p>
          </div>
          <div className='min-w-[220px] w-full sm:w-72'>
            <BrandSelect
              styles={adminToolbarSelect}
              options={ownerOptions}
              value={selectedOwner}
              onChange={handleOwnerChange}
              placeholder={ownerReady ? 'Select account manager' : 'Loading…'}
              isDisabled={!ownerReady || !!ownerLoading}
              isSearchable
            />
          </div>
        </CardContent>
      </Card>

      <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
        <Card className='bg-card'>
          <CardContent className='p-4 text-center'>
            <p className='text-2xl font-bold'>{completenessHeadline}</p>
            <p className='text-xs text-muted-foreground'>
              Completeness
              {health.reportable === true ? ' · Reportable' : health.reportable === false ? ' · Not reportable' : ''}
            </p>
            {health.completenessReason && completenessHeadline === 'Not computed' && (
              <p className='text-xs text-muted-foreground mt-1'>{health.completenessReason}</p>
            )}
          </CardContent>
        </Card>
        <Card className='bg-card'>
          <CardContent className='p-4 text-center'>
            {qualityScore != null ? (
              <QualityTierBadge level={Math.round(qualityScore)} />
            ) : (
              <p className='text-2xl font-bold'>Not computed</p>
            )}
            <p className='text-xs text-muted-foreground mt-1'>Item quality (adjacent)</p>
          </CardContent>
        </Card>
        <Card className='bg-card'>
          <CardContent className='p-4 text-center'>
            <div className='flex items-center justify-center gap-1'>
              {delta != null && <DeltaIcon size={18} strokeWidth={1.5} />}
              <p className='text-2xl font-bold'>
                {delta != null
                  ? `${delta > 0 ? '+' : ''}${Number(delta).toFixed(1)}%`
                  : yoy.basis === 'first_year'
                    ? 'First year'
                    : 'Not computed'}
              </p>
            </div>
            <p className='text-xs text-muted-foreground'>{yoy.reportingPeriodLabel || 'YoY emissions'}</p>
          </CardContent>
        </Card>
        <Card className='bg-card'>
          <CardContent className='p-4 text-center'>
            <Badge variant='secondary'>
              {reportStatusLabel[health.reportStatus] || 'Not computed'}
            </Badge>
            <p className='text-xs text-muted-foreground mt-2'>Report status</p>
          </CardContent>
        </Card>
      </div>

      <Card className='bg-card'>
        <CardHeader className='pb-2'>
          <CardTitle className='text-sm'>Readiness</CardTitle>
        </CardHeader>
        <CardContent className='space-y-3'>
          <p className='text-sm text-foreground'>{completenessHeadline}</p>
          {health.usageDropThresholdPct != null && (
            <p className='text-xs text-muted-foreground'>
              Usage drop threshold {health.usageDropThresholdPct}%
              {health.completenessMode ? ` · mode ${health.completenessMode}` : ''}
            </p>
          )}
          {[
            ['Definitely missing', Array.isArray(lists.definitelyMissing) ? lists.definitelyMissing : []],
            ['Unit changes', Array.isArray(lists.unitChanges) ? lists.unitChanges : []],
            [
              'Identified substitutions',
              Array.isArray(lists.identifiedSubstitutions) ? lists.identifiedSubstitutions : [],
            ],
            [
              'Discrepancy risks',
              Array.isArray(lists.discrepancyRisks)
                ? lists.discrepancyRisks
                : lists.discrepancyRisks?.notComputed
                  ? [{ category: lists.discrepancyRisks.reason || 'Not computed' }]
                  : [],
            ],
            ['New categories', Array.isArray(lists.newCategories) ? lists.newCategories : []],
          ].map(([label, items]) => (
            <div key={label}>
              <p className='text-xs font-medium text-muted-foreground mb-1'>
                {label} ({(items || []).length})
              </p>
              {(items || []).length === 0 ? (
                <p className='text-xs text-muted-foreground'>None</p>
              ) : (
                <ul className='text-sm space-y-0.5'>
                  {(items || []).slice(0, 20).map((item, idx) => (
                    <li key={`${label}-${idx}`}>
                      {item.category || item.name || 'Category'}
                      {item.percentageFall != null ? ` · −${Number(item.percentageFall).toFixed(1)}%` : ''}
                      {item.unit ? ` · ${item.unit}` : ''}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className='bg-card'>
        <CardHeader className='pb-2'>
          <CardTitle className='text-sm'>Category YoY</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className='border-border'>
                <TableHead>Category</TableHead>
                <TableHead>Current</TableHead>
                <TableHead>Prior</TableHead>
                <TableHead>Δ%</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(health.categoryBreakdown || []).map((row, idx) => (
                <TableRow key={`${row.entity}-${row.subentity}-${idx}`} className='border-border'>
                  <TableCell>{row.category || row.subentity}</TableCell>
                  <TableCell>{row.current ?? '—'}</TableCell>
                  <TableCell>{row.prior ?? '—'}</TableCell>
                  <TableCell>{row.deltaPct != null ? `${row.deltaPct}%` : '—'}</TableCell>
                </TableRow>
              ))}
              {(health.categoryBreakdown || []).length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className='text-center text-muted-foreground py-4'>
                    No category YoY yet (needs paired report summaries).
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className='bg-card'>
        <CardHeader className='pb-2'>
          <CardTitle className='text-sm'>Open worklist</CardTitle>
        </CardHeader>
        <CardContent className='space-y-2'>
          <div className='flex flex-wrap items-center justify-between gap-2 text-sm'>
            <span>
              Open {(health.openWorklist || []).length}
              {' · '}
              Resolved{' '}
              {(health.openWorklistResolvedCount != null && health.openWorklistResolvedCount) || 0}
            </span>
            <label className='flex items-center gap-2 text-xs'>
              <input
                type='checkbox'
                checked={!!health.engagement?.respondingPartyAccess}
                disabled={accessBusy || !!ownerLoading}
                onChange={async (e) => {
                  setAccessBusy(true);
                  const result = await setRespondingPartyAccess?.(health.clientOrgId, e.target.checked);
                  setAccessBusy(false);
                  if (result?.ok) {
                    showAlert(
                      true,
                      e.target.checked ? 'Responding-party access on.' : 'Responding-party access off.',
                      'success',
                      4000,
                    );
                    onRefresh?.();
                  } else {
                    showAlert(true, result?.message || 'Could not update access.', 'error', 5000);
                  }
                }}
              />
              Responding-party access
            </label>
          </div>
          {(health.openWorklist || []).length === 0 ? (
            <p className='text-sm text-muted-foreground'>{health.openWorklistNote || 'Nothing waiting'}</p>
          ) : (
            <ul className='text-sm space-y-1'>
              {health.openWorklist.map((item) => (
                <li key={item.id} className='flex justify-between gap-2'>
                  <button
                    type='button'
                    className='text-left text-primary hover:underline'
                    onClick={() => onOpenTask?.(item.id)}>
                    {item.title || item.subject || item.id}
                  </button>
                  <span className='text-xs text-muted-foreground'>
                    {item.status || (item.taskHolder === 'responding' || item.holder === 'client'
                      ? 'Waiting on client'
                      : 'Waiting on us')}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <div className='flex flex-wrap gap-2 pt-2 items-end'>
            <div className='flex-1 min-w-[180px]'>
              <input
                className='w-full rounded-md border border-border bg-input px-3 py-2 text-sm'
                placeholder='Custom work item title'
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
              />
            </div>
            <Button
              variant='outline'
              size='sm'
              disabled={!!ownerLoading}
              onClick={async () => {
                const title = newTitle.trim() || 'Follow up on readiness';
                const result = await createWorkItem?.(health.clientOrgId, {
                  title,
                  priority: 'med',
                });
                if (result?.ok) {
                  setNewTitle('');
                  showAlert(true, 'Work item created.', 'success', 4000);
                  onRefresh?.();
                  if (result.item?.id) onOpenTask?.(result.item.id);
                } else {
                  showAlert(true, result?.message || 'Could not create work item.', 'error', 5000);
                }
              }}>
              Add work item
            </Button>
            <Button
              variant='outline'
              size='sm'
              disabled={!!ownerLoading}
              onClick={async () => {
                const result = await createWorkItem?.(health.clientOrgId, {
                  title: newTitle.trim() || 'Readiness question',
                  priority: 'med',
                  shared: true,
                });
                if (result?.ok) {
                  setNewTitle('');
                  showAlert(true, 'Work item created — assign a client contact to hand off.', 'success', 5000);
                  onRefresh?.();
                  if (result.item?.id) onOpenTask?.(result.item.id);
                } else {
                  showAlert(true, result?.message || 'Could not create work item.', 'error', 5000);
                }
              }}>
              Ask readiness question
            </Button>
            {health.reportable !== true && (
              <Button
                size='sm'
                disabled={!!ownerLoading}
                onClick={async () => {
                  const result = await signOffCompleteness?.(health.clientOrgId);
                  if (result?.ok) {
                    showAlert(true, 'Completeness signed off — reportable.', 'success', 4000);
                    onRefresh?.();
                  } else {
                    showAlert(true, result?.message || 'Sign-off failed.', 'error', 5000);
                  }
                }}>
                Sign off completeness
              </Button>
            )}
            <Button
              variant='ghost'
              size='sm'
              disabled={!!ownerLoading}
              onClick={async () => {
                const result = await requestAssist?.({
                  kind: 'explain_movement',
                  clientOrgId: health.clientOrgId,
                });
                if (result?.ok && result.draft?.body) {
                  showAlert(true, result.draft.body, 'info', 8000);
                } else {
                  showAlert(true, result?.message || 'Assist unavailable.', 'error', 5000);
                }
              }}>
              Explain movement
            </Button>
          </div>
        </CardContent>
      </Card>

      {health.spendReconciliationBasis === 'connector_pending' && (
        <p className='text-xs text-muted-foreground'>
          Spend reconciliation pending Xero rebuild integration — not shown as fabricated data.
        </p>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  organisationUsers: state.organisation?.users || [],
  ownerLoading: state.consultantDashboard?.loading?.owner,
});

const mapDispatchToProps = (dispatch) => ({
  getOrganisationUsers: () => dispatch(ORG_ACTIONS.getOrganisationUsers()),
  assignClientOwner: (orgId, ownerUserId) => dispatch(DASHBOARD_ACTIONS.assignClientOwner(orgId, ownerUserId)),
  createWorkItem: (orgId, body) => dispatch(DASHBOARD_ACTIONS.createWorkItem(orgId, body)),
  setRespondingPartyAccess: (orgId, enabled) =>
    dispatch(DASHBOARD_ACTIONS.setRespondingPartyAccess(orgId, enabled)),
  signOffCompleteness: (orgId) => dispatch(DASHBOARD_ACTIONS.signOffCompleteness(orgId)),
  requestAssist: (body) => dispatch(DASHBOARD_ACTIONS.requestAssist(body)),
  showAlert: (status, message, severity, hideDuration) =>
    dispatch(toggleAlertMessage(status, message, severity, hideDuration)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ClientHealthDashboard);
